/**
 * What happened to the selected period, one line per borrower's current run.
 *
 * Built from the BOARD rows rather than from an endpoint of its own: the board
 * already carries every run's version, status and timestamps, so a second
 * request would ask the server for what the page is holding. The consequence is
 * honest and stated on screen — this is the LATEST version per borrower, not
 * every version ever generated. Per-run version history lives on the run page
 * (DOP-452), which is where a version you want to compare against is useful.
 */

import React from "react";
import { Link } from "react-router-dom";
import type { MonitoringBoardRow } from "../../types/monitoring";
import { formatEpochMs } from "./monitoringFormat";
import { runPath } from "./PeriodBoard";

const CELL = "px-3 py-2 align-top";
const HEAD = `${CELL} text-sm font-medium text-text-muted`;

interface RunHistoryListProps {
	rows: MonitoringBoardRow[];
	periodLabel: string;
}

const RunHistoryList: React.FC<RunHistoryListProps> = ({
	rows,
	periodLabel,
}) => {
	const withRuns = rows.filter((row) => row.run !== null);

	if (withRuns.length === 0) {
		return (
			<p className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-text-muted">
				Nothing has been run for {periodLabel} yet.
			</p>
		);
	}

	return (
		<section>
			<p className="mb-3 text-sm text-text-muted">
				The current run for each borrower in {periodLabel}. Earlier versions of
				a run are listed on that run's own page.
			</p>
			<div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
				<table className="w-full border-collapse text-base">
					<thead>
						<tr className="border-b border-gray-200 bg-gray-50 text-left">
							<th className={HEAD}>Borrower</th>
							<th className={HEAD}>Version</th>
							<th className={HEAD}>Status</th>
							<th className={HEAD}>Generated</th>
							<th className={HEAD}>Locked</th>
							<th className={HEAD}>
								<span className="sr-only">Action</span>
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-100">
						{withRuns.map((row) => {
							// `withRuns` filtered these, but the compiler cannot see that
							// through `filter`, and a non-null assertion would be a claim
							// rather than a check.
							const run = row.run;
							if (!run) return null;
							return (
								<tr key={row.borrower.public_id}>
									<td className={CELL}>{row.borrower.name}</td>
									<td className={CELL}>v{run.version}</td>
									<td className={CELL}>
										{run.generating ? "Generating…" : run.status}
									</td>
									<td className={CELL}>{formatEpochMs(run.generated_at)}</td>
									<td className={CELL}>{formatEpochMs(run.locked_at)}</td>
									<td className={CELL}>
										<Link
											to={runPath(run.public_id)}
											className="text-sm font-medium text-[#008782] hover:underline"
										>
											Open
										</Link>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</section>
	);
};

export default RunHistoryList;
